import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { selectGroupGuid } from 'slices/commonSelectors';
import { DGPTAlertCategory } from 'types/dgptAlerts';

export const keys = {
  alertCategories: 'alertCategories',
  all: ['alertCategories'],
  group: (groupGuid: string) => ['alertCategories', groupGuid],
} as const;

export interface UpdateAlertCategoryPayload {
  id: string;
  teams?: string[];
  contactMethods?: string[];
  severity: number;
  ack?: boolean;
}

// Fetch alert categories
export const useAlertCategories = () => {
  const groupGuid = useSelector(selectGroupGuid);

  return useQuery<DGPTAlertCategory[], Error>({
    queryKey: keys.group(groupGuid),
    queryFn: async () => {
      if (!groupGuid) return [];

      try {
        const response = await axios.get(
          `${config.apiGateway.dgptAlerts}/alert-categories`,
          {
            params: { groupGuid },
          }
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching alert categories:', error);
        toast.error('Failed to fetch alert categories.');
        throw error;
      }
    },
    enabled: !!groupGuid,
  });
};

export const useGenerateDefaultCatetories = () => {
  const queryClient = useQueryClient();
  const groupGuid = useSelector(selectGroupGuid);

  return useMutation<void, Error, UUID | undefined>({
    mutationFn: async (defaultTeam?: UUID) => {
      if (!defaultTeam) {
        throw new Error(
          'Default team GUID is required to generate categories.'
        );
      }

      await axios.post(`${config.apiGateway.dgptAlertsGenerateCategories}`, {
        groupGuid,
        teamGuid: defaultTeam,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.group(groupGuid),
      });
      toast.success('Default alert categories generated successfully.');
    },
    onError: (error) => {
      console.error('Error in mutation:', error);
      toast.error('Failed to generate default alert categories.');
    },
  });
};

export const useSetDefaultTeamForCategories = () => {
  const queryClient = useQueryClient();
  const groupGuid = useSelector(selectGroupGuid);

  return useMutation<void, Error, string>({
    mutationFn: async (teamId: string) => {
      if (!teamId) {
        throw new Error(
          'Team ID is required to set default team for categories.'
        );
      }

      await axios.post(
        `${config.apiGateway.dgptAlerts}/setDefaultTeamForCategories`,
        {
          groupGuid,
          teamId,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.group(groupGuid),
      });
      toast.success('Default team set for categories successfully.');
    },
    onError: (error) => {
      console.error('Error in mutation:', error);
      toast.error('Failed to set default team for categories.');
    },
  });
};

// Update an alert category
export const useUpdateAlertCategory = () => {
  const queryClient = useQueryClient();
  const groupGuid = useSelector(selectGroupGuid);

  return useMutation<DGPTAlertCategory, Error, UpdateAlertCategoryPayload>({
    mutationFn: async (data) => {
      const { id, ...updateData } = data;
      const response = await axios.put(
        `${config.apiGateway.dgptAlerts}/alert-categories/${id}`,
        updateData
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.group(groupGuid),
      });
      toast.success('Alert category updated successfully.');
    },
    onError: (error) => {
      console.error('Error updating alert category:', error);
      toast.error('Failed to update alert category.');
    },
  });
};
